/*
 * Account panel — the auth state machine UI, ported from the settings panel.
 * Renders one of three sub-views based on store.authState:
 *   logged-out → otp-pending → logged-in
 */

import { useState, type FormEvent } from "react";
import {
  Avatar,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Subtitle2,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useStore } from "../../state/store";
import { useTranslation } from "../../i18n/useTranslation";
import type { TranslationStrings } from "../../i18n/translations";
import { useAuth } from "./useAuth";
import { formatSubscriptionPlanLabel } from "../../state/subscriptionLabels";
import { buildPortalUrl } from "../common/portalLinks";
import { ApiRequestError, type AccountDevPersona } from "../../api/client";
const DEV_PERSONAS: AccountDevPersona[] = ["author", "editor", "publisher", "reviewer"];

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxWidth: "420px",
    margin: "0 auto",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  devPersonas: {
    display: "grid",
    gap: "8px",
    padding: "10px",
    border: `1px dashed ${tokens.colorPaletteDarkOrangeBorderActive}`,
    borderRadius: "6px",
    backgroundColor: tokens.colorPaletteDarkOrangeBackground1,
  },
  devPersonaActions: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "6px",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    margin: "4px 0",
    color: tokens.colorNeutralForeground4,
    "::before": {
      content: '""',
      flex: 1,
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      marginRight: "8px",
    },
    "::after": {
      content: '""',
      flex: 1,
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      marginLeft: "8px",
    },
  },
  passkeySection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  passkeyHint: {
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },
});

/*
 * Maps WebAuthn and API error variants to localized user-friendly messages.
 * Prevents account enumeration by treating credential mismatches, user lookups,
 * and unknown failures indistinguishably as a generic passkey failure.
 * Never leaks raw backend error messages or stack details to the UI.
 */
export function getAuthErrorMessage(err: unknown, t: TranslationStrings): string {
  if (err instanceof ApiRequestError) {
    if (err.code === "FEATURE_DISABLED" || err.code === "PASSKEYS_DISABLED" || err.status === 403) {
      return t.authPasskeyFeatureDisabled;
    }
    if (
      err.code === "INVALID_CHALLENGE" ||
      err.code === "EXPIRED_CHALLENGE" ||
      err.code === "CHALLENGE_EXPIRED"
    ) {
      return t.authPasskeyChallengeExpired;
    }
    return t.authPasskeyFailed;
  }
  if (err instanceof Error) {
    switch (err.message) {
      case "WEBAUTHN_CANCELLED":
        return t.authPasskeyCancelled;
      case "WEBAUTHN_NOT_SUPPORTED":
        return t.authPasskeyNotSupported;
      case "WEBAUTHN_SECURITY_RESTRICTED":
        return t.authPasskeySecurityRestricted;
      case "NO_PASSKEY_OPTIONS":
        return t.authPasskeyChallengeExpired;
      default:
        return t.authPasskeyFailed;
    }
  }
  return t.authPasskeyFailed;
}

function LoggedOutForm(): JSX.Element {
  const styles = useStyles();
  const { t } = useTranslation();
  const { requestOtp, devLogin, loginWithPasskey } = useAuth();
  const pendingEmail = useStore((s) => s.pendingEmail);
  const [email, setEmail] = useState(pendingEmail);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passkeySubmitting, setPasskeySubmitting] = useState(false);
  const [error, setError] = useState("");
  const isWebAuthnSupported = browserSupportsWebAuthn();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setSubmitting(true);
    setError("");
    try {
      await requestOtp(value, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onPasskeyLogin = async () => {
    setPasskeySubmitting(true);
    setError("");
    try {
      const hint = email.trim() || undefined;
      await loginWithPasskey(hint);
    } catch (err) {
      setError(getAuthErrorMessage(err, t));
    } finally {
      setPasskeySubmitting(false);
    }
  };

  const onDevLogin = async (persona: AccountDevPersona): Promise<void> => {
    setSubmitting(true);
    setError("");
    try {
      await devLogin(persona);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = submitting || passkeySubmitting;

  return (
    <Card className={styles.container}>
      <Subtitle2>{t.authEmailTitle}</Subtitle2>
      <Caption1 className={styles.subtitle}>{t.authEmailSubtitle}</Caption1>
      <form className={styles.form} onSubmit={onSubmit}>
        <Field label={t.authEmailLabel}>
          <Input
            type="email"
            value={email}
            placeholder={t.authEmailPlaceholder}
            onChange={(_e, data) => setEmail(data.value)}
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            appearance="outline"
            value={password}
            placeholder="Your password"
            onChange={(_e, data) => setPassword(data.value)}
          />
        </Field>
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}
        <Button type="submit" appearance="primary" disabled={isBusy}>
          {submitting ? t.authEmailSending : t.authEmailBtn}
        </Button>
      </form>
      {isWebAuthnSupported ? (
        <div className={styles.passkeySection}>
          <div className={styles.divider}>
            <Caption1>{t.authPasskeyOr}</Caption1>
          </div>
          <Button
            appearance="secondary"
            disabled={isBusy}
            onClick={() => void onPasskeyLogin()}
          >
            {passkeySubmitting ? t.authPasskeySigningIn : t.authPasskeyBtn}
          </Button>
          <Caption1 className={styles.passkeyHint}>{t.authPasskeyHint}</Caption1>
        </div>
      ) : null}
      {typeof __ACCOUNT_DEV_AUTH_UI__ !== "undefined" && __ACCOUNT_DEV_AUTH_UI__ ? (
        <div className={styles.devPersonas}>
          <Caption1 className={styles.subtitle}>{t.authDevPersonaHint}</Caption1>
          <div className={styles.devPersonaActions}>
            {DEV_PERSONAS.map((persona) => (
              <Button
                key={persona}
                appearance="outline"
                disabled={isBusy}
                onClick={() => void onDevLogin(persona)}
              >
                {t.authDevPersonas[persona]}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function OtpForm(): JSX.Element {
  const styles = useStyles();
  const { t } = useTranslation();
  const { verifyOtp, goToLoggedOut } = useAuth();
  const [code, setCode] = useState("");
  const [markTrusted, setMarkTrusted] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const value = code.trim();
    if (!value) return;
    setSubmitting(true);
    setError("");
    try {
      await verifyOtp({
        code: value,
        markTrusted,
        trustedDeviceLabel: deviceName.trim() || navigator.userAgent.slice(0, 40),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className={styles.container}>
      <Subtitle2>{t.authOtpTitle}</Subtitle2>
      <Caption1 className={styles.subtitle}>{t.authOtpSubtitle}</Caption1>
      <form className={styles.form} onSubmit={onSubmit}>
        <Field label={t.authOtpLabel}>
          <Input
            value={code}
            placeholder={t.authOtpPlaceholder}
            inputMode="numeric"
            onChange={(_e, data) => setCode(data.value)}
          />
        </Field>
        <Checkbox
          checked={markTrusted}
          onChange={(_e, data) => setMarkTrusted(Boolean(data.checked))}
          label={t.authOtpTrustLabel}
        />
        <Caption1 className={styles.subtitle}>{t.authOtpTrustHint}</Caption1>
        {markTrusted ? (
          <Field label={t.authOtpDeviceLabel}>
            <Input
              value={deviceName}
              placeholder={t.authOtpDevicePlaceholder}
              onChange={(_e, data) => setDeviceName(data.value)}
            />
          </Field>
        ) : null}
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}
        <Button type="submit" appearance="primary" disabled={submitting}>
          {submitting ? t.authOtpVerifying : t.authOtpBtn}
        </Button>
        <Button appearance="subtle" onClick={goToLoggedOut}>
          {t.authOtpBack}
        </Button>
      </form>
    </Card>
  );
}

function LoggedInSummary(): JSX.Element {
  const styles = useStyles();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const user = useStore((s) => s.sessionUser);
  const subscription = useStore((s) => s.subscription);

  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "—";

  const planLabel = formatSubscriptionPlanLabel(subscription, {
    free: t.tokenPickerNoPlan,
    families: t.planFamilies,
    tiers: t.planTiers,
  });

  const periodEnd = subscription?.subscription?.currentPeriodEnd;
  const renewDate = periodEnd ? new Date(periodEnd).toLocaleDateString() : "";

  const openPortalPath = (path: string) => {
    window.open(buildPortalUrl(path), "_blank", "noopener,noreferrer");
  };

  return (
    <Card className={`${styles.container} account-card`}>
      <div className={styles.summaryHeader}>
        <Avatar name={name !== "—" ? name : undefined} color="colorful" />
        <div>
          <Body1>{name}</Body1>
          <Caption1 className={styles.subtitle}>{user?.email || "—"}</Caption1>
        </div>
      </div>
      <div className="account-token-strip">
        <Caption1 className={styles.subtitle}>{t.loggedInPlan}</Caption1>
        <Body1 className="account-token-strip__value">{planLabel}</Body1>
      </div>
      {renewDate ? (
        <div className="account-token-strip">
          <Caption1 className={styles.subtitle}>{t.loggedInRenews}</Caption1>
          <Body1 className="account-token-strip__value">{renewDate}</Body1>
        </div>
      ) : null}
      <div className="account-action-section">
        <Caption1 className={styles.subtitle}>{t.loggedInPortalLinks}</Caption1>
        <div className="account-link-list">
          <Button
            className="account-link-button"
            appearance="primary"
            onClick={() => openPortalPath("/plans")}
          >
            {t.loggedInUpgrade}
          </Button>
          <Button
            className="account-link-button"
            appearance="subtle"
            onClick={() => openPortalPath("/account/billing")}
          >
            {t.loggedInBillingLegal}
          </Button>
          <Button
            className="account-link-button"
            appearance="subtle"
            onClick={() => openPortalPath("/terms")}
          >
            {t.loggedInTerms}
          </Button>
          <Button
            className="account-link-button"
            appearance="subtle"
            onClick={() => openPortalPath("/privacy")}
          >
            {t.loggedInPrivacy}
          </Button>
        </div>
      </div>
      <div className="account-session-actions">
        <Button
          className="account-session-button account-session-button--danger"
          appearance="subtle"
          onClick={logout}
        >
          {t.loggedInLogout}
        </Button>
      </div>
    </Card>
  );
}

export function AccountPanel(): JSX.Element {
  const authState = useStore((s) => s.authState);
  switch (authState) {
    case "otp-pending":
      return <OtpForm />;
    case "logged-in":
      return <LoggedInSummary />;
    case "logged-out":
    default:
      return <LoggedOutForm />;
  }
}
